<template>
  <div class="format-converter">
    <!-- ============ ENGLISH ============ -->
    <div class="compressor-wrapper" v-if="currentLanguage == 'en'">
      
      <!-- Drop Zone -->
      <div
        class="drop-zone"
        :class="{ dragging: isDragging }"
        v-if="selectedFiles.length === 0"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="$refs.fileInput.click()"
      >
        <div class="drop-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
            <path d="M142.9 142.9c-17.5 17.5-30.1 38-37.8 59.8c-5.9 16.7-24.2 25.4-40.8 19.5s-25.4-24.2-19.5-40.8C55.6 150.7 73.2 122 97.6 97.6c87.2-87.2 228.3-87.5 315.8-1L455 55c6.9-6.9 17.2-8.9 26.5-5.3s15.4 11.9 15.4 21.6V184c0 13.3-10.7 24-24 24H360c-9.7 0-18-5.8-21.6-15.4s-1.6-19.6 5.3-26.5l41.6-41.6c-60.5-59.9-157.4-59.7-217.4 .8zM369.1 369.1c17.5-17.5 30.1-38 37.8-59.8c5.9-16.7 24.2-25.4 40.8-19.5s25.4 24.2 19.5 40.8c-10.8 30.6-28.4 59.3-52.9 83.8c-87.2 87.2-228.3 87.5-315.8 1L57 457c-6.9 6.9-17.2 8.9-26.5 5.3S15 450.4 15 440.7V328c0-13.3 10.7-24 24-24H152c9.7 0 18 5.8 21.6 15.4s1.6 19.6-5.3 26.5l-41.6 41.6c60.5 59.9 157.4 59.7 217.4-.8z"/>
          </svg>
        </div>
        <p class="drop-text">Drag & Drop images here</p>
        <p class="drop-hint">or</p>
        <button class="choose-btn" @click.stop="$refs.fileInput.click()">Choose Files</button>
        <p class="drop-formats">Supports JPG, PNG, WebP, AVIF, SVG</p>
      </div>

      <!-- Settings Panel (Always Visible) -->
      <div class="settings-grid mb-4">
        <div class="settings-card" style="grid-column: 1 / -1; display: flex; flex-wrap: wrap; gap: 20px; align-items: flex-end;">
          <div style="flex: 1; min-width: 200px;">
            <label class="field-label">Convert To:</label>
            <div class="select-wrap">
              <select v-model="targetFormat">
                <option value="image/webp">WebP (Best for Web Speed)</option>
                <option value="image/jpeg">JPG (Most Compatible)</option>
                <option value="image/png">PNG (Preserves Transparency)</option>
              </select>
              <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>
          
          <div style="flex: 1; min-width: 200px;">
            <label class="field-label">Quality:</label>
            <div class="select-wrap">
              <select v-model="quality">
                <option value="1">Best Quality (100%)</option>
                <option value="0.85">Balanced (85%)</option>
                <option value="0.6">Small Size (60%)</option>
              </select>
              <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>

          <div style="flex: 0 0 auto;">
              <button class="download-btn" @click="convertAll" :disabled="isProcessing || selectedFiles.length === 0" style="width: 100%;">
                <svg v-if="!isProcessing" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.36"/></svg>
                <span v-if="isProcessing">Processing...</span>
                <span v-else>Convert All Images</span>
              </button>
          </div>
        </div>
      </div>

      <!-- File List -->
      <div class="file-list-container" v-if="selectedFiles.length > 0">
        <div class="preview-label" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
          <span>Uploaded Files ({{ selectedFiles.length }})</span>
          <div style="display: flex; gap: 10px;">
            <button class="reset-icon-btn" @click="$refs.fileInput.click()" style="color: #3a7afe;">+ Add More</button>
            <button class="reset-icon-btn" @click="reset">✕ Clear All</button>
          </div>
        </div>

        <div class="file-grid">
          <div v-for="(file, index) in selectedFiles" :key="file.id" class="file-item">
            <img :src="file.originalUrl" class="file-thumb" />
            
            <div class="file-info">
              <div class="file-name" :title="file.originalFile.name">{{ file.originalFile.name }}</div>
              <div class="file-meta">
                <span class="original-badge">{{ formatSize(file.originalFile.size) }}</span>
                <span v-if="file.convertedUrl"> ➔ </span>
                <span v-if="file.convertedUrl" class="converted-badge">{{ formatSize(file.convertedSize) }}</span>
              </div>
            </div>

            <div class="file-action">
              <a v-if="file.convertedUrl" :href="file.convertedUrl" :download="file.newName" class="icon-action-btn success" title="Download">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </a>
              <div v-else-if="isProcessing && file.isConverting" class="spinner-small"></div>
              <button v-else @click="deleteFile(index)" class="icon-action-btn danger" title="Remove">✕</button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- ============ ARABIC ============ -->
    <div class="compressor-wrapper" v-else>
      
      <!-- Drop Zone -->
      <div
        class="drop-zone"
        :class="{ dragging: isDragging }"
        v-if="selectedFiles.length === 0"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="$refs.fileInput.click()"
      >
        <div class="drop-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M142.9 142.9c-17.5 17.5-30.1 38-37.8 59.8c-5.9 16.7-24.2 25.4-40.8 19.5s-25.4-24.2-19.5-40.8C55.6 150.7 73.2 122 97.6 97.6c87.2-87.2 228.3-87.5 315.8-1L455 55c6.9-6.9 17.2-8.9 26.5-5.3s15.4 11.9 15.4 21.6V184c0 13.3-10.7 24-24 24H360c-9.7 0-18-5.8-21.6-15.4s-1.6-19.6 5.3-26.5l41.6-41.6c-60.5-59.9-157.4-59.7-217.4 .8zM369.1 369.1c17.5-17.5 30.1-38 37.8-59.8c5.9-16.7 24.2-25.4 40.8-19.5s25.4 24.2 19.5 40.8c-10.8 30.6-28.4 59.3-52.9 83.8c-87.2 87.2-228.3 87.5-315.8 1L57 457c-6.9 6.9-17.2 8.9-26.5 5.3S15 450.4 15 440.7V328c0-13.3 10.7-24 24-24H152c9.7 0 18 5.8 21.6 15.4s1.6 19.6-5.3 26.5l-41.6 41.6c60.5 59.9 157.4 59.7 217.4-.8z"/></svg>
        </div>
        <p class="drop-text">اسحب الصور هنا أو اضغط للاختيار</p>
        <p class="drop-formats" style="margin-top: 5px;">يدعم JPG, PNG, WebP, AVIF, SVG</p>
        <button class="choose-btn" style="margin-top: 15px;" @click.stop="$refs.fileInput.click()">اختر الملفات</button>
      </div>

      <!-- Settings Panel (Always Visible) -->
      <div class="settings-grid mb-4">
        <div class="settings-card" style="grid-column: 1 / -1; display: flex; flex-wrap: wrap; gap: 20px; align-items: flex-end;">
          <div style="flex: 1; min-width: 200px;">
            <label class="field-label">تحويل إلى:</label>
            <div class="select-wrap">
              <select v-model="targetFormat">
                <option value="image/webp">WebP (الأفضل لسرعة المواقع)</option>
                <option value="image/jpeg">JPG (الأكثر توافقاً)</option>
                <option value="image/png">PNG (للحفاظ على الشفافية)</option>
              </select>
              <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>
          
          <div style="flex: 1; min-width: 200px;">
            <label class="field-label">الجودة:</label>
            <div class="select-wrap">
              <select v-model="quality">
                <option value="1">أفضل جودة (100%)</option>
                <option value="0.85">متوازنة (85%)</option>
                <option value="0.6">حجم صغير (60%)</option>
              </select>
              <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>

          <div style="flex: 0 0 auto;">
              <button class="download-btn" @click="convertAll" :disabled="isProcessing || selectedFiles.length === 0" style="width: 100%;">
                <svg v-if="!isProcessing" style="transform: scaleX(-1);" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.36"/></svg>
                <span v-if="isProcessing">جاري التحويل...</span>
                <span v-else>ابدأ تحويل الصور الآن</span>
              </button>
          </div>
        </div>
      </div>

      <!-- File List -->
      <div class="file-list-container" v-if="selectedFiles.length > 0">
        <div class="preview-label" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
          <span>الملفات المرفوعة ({{ selectedFiles.length }})</span>
          <div style="display: flex; gap: 10px;">
            <button class="reset-icon-btn" @click="$refs.fileInput.click()" style="color: #3a7afe;">+ إضافة المزيد</button>
            <button class="reset-icon-btn" @click="reset">✕ مسح الكل</button>
          </div>
        </div>

        <div class="file-grid">
          <div v-for="(file, index) in selectedFiles" :key="file.id" class="file-item">
            <img :src="file.originalUrl" class="file-thumb" />
            
            <div class="file-info">
              <div class="file-name" :title="file.originalFile.name">{{ file.originalFile.name }}</div>
              <div class="file-meta">
                <span class="original-badge">{{ formatSize(file.originalFile.size) }}</span>
                <span v-if="file.convertedUrl"> ➔ </span>
                <span v-if="file.convertedUrl" class="converted-badge">{{ formatSize(file.convertedSize) }}</span>
              </div>
            </div>

            <div class="file-action">
              <a v-if="file.convertedUrl" :href="file.convertedUrl" :download="file.newName" class="icon-action-btn success" title="تحميل">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </a>
              <div v-else-if="isProcessing && file.isConverting" class="spinner-small"></div>
              <button v-else @click="deleteFile(index)" class="icon-action-btn danger" title="حذف">✕</button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Hidden Input -->
    <input ref="fileInput" type="file" accept="image/*" multiple style="display:none" @change="handleFileUpload" />
  </div>
</template>

<script>
import { notifyToolSuccess } from '@/utils/promoPopup';

export default {
  name: 'FormatConverterCom',
  data() {
    return {
      currentLanguage: this.$store.state.currentLanguage,
      isDragging: false,
      selectedFiles: [],
      targetFormat: 'image/webp',
      quality: '0.85',
      isProcessing: false,
    };
  },
  watch: {
    '$store.state.currentLanguage': function(newLanguage) {
      this.currentLanguage = newLanguage;
    }
  },
  methods: {
    handleDrop(e) {
      this.isDragging = false;
      const files = e.dataTransfer.files;
      if (files && files.length > 0) this.processFiles(files);
    },
    handleFileUpload(e) {
      const files = e.target.files;
      if (files && files.length > 0) this.processFiles(files);
      this.$refs.fileInput.value = '';
    },
    processFiles(files) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith('image/')) {
          const url = window.URL.createObjectURL(file);
          this.selectedFiles.push({
            id: Date.now() + Math.random(),
            originalFile: file,
            originalUrl: url,
            convertedUrl: null,
            convertedSize: 0,
            newName: '',
            isConverting: false
          });
        }
      }
    },
    deleteFile(index) {
      const file = this.selectedFiles[index];
      if (file.originalUrl) URL.revokeObjectURL(file.originalUrl);
      if (file.convertedUrl) URL.revokeObjectURL(file.convertedUrl);
      this.selectedFiles.splice(index, 1);
    },
    reset() {
      this.selectedFiles.forEach(file => {
        if (file.originalUrl) URL.revokeObjectURL(file.originalUrl);
        if (file.convertedUrl) URL.revokeObjectURL(file.convertedUrl);
      });
      this.selectedFiles = [];
    },
    async convertAll() {
      if (this.selectedFiles.length === 0 || this.isProcessing) return;
      this.isProcessing = true;

      for (let i = 0; i < this.selectedFiles.length; i++) {
        const item = this.selectedFiles[i];
        
        if (item.convertedUrl) continue;
        
        item.isConverting = true;

        await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            
            if (this.targetFormat === 'image/jpeg') {
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
            
            ctx.drawImage(img, 0, 0);
            
            canvas.toBlob((blob) => {
              if (blob) {
                item.convertedUrl = URL.createObjectURL(blob);
                item.convertedSize = blob.size;
                const ext = this.targetFormat.split('/')[1];
                item.newName = item.originalFile.name.replace(/\.[^/.]+$/, "") + `_splendapp.${ext}`;
              }
              item.isConverting = false;
              resolve();
            }, this.targetFormat, parseFloat(this.quality));
          };
          img.onerror = () => {
            item.isConverting = false;
            resolve();
          };
          img.src = item.originalUrl;
        });
      }

      this.isProcessing = false;
      if (this.selectedFiles.some(item => item.convertedUrl)) {
        notifyToolSuccess('format-converter');
      }
    },
    formatSize(bytes) {
      if (!bytes) return '—';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400&family=DM+Mono:wght@400;500&display=swap');

* { box-sizing: border-box; }

.compressor-wrapper {
  font-family: 'DM Sans', 'Cairo', sans-serif;
  max-width: 960px; margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
  background: #F6F9FB;
  min-height: 100vh;
}
[dir="rtl"] .compressor-wrapper { direction: rtl; text-align: right; }

/* ── Drop Zone ── */
.drop-zone {
  background: white; border: 2.5px dashed #3a7afe; border-radius: 20px;
  padding: 3rem 2rem; text-align: center; cursor: pointer;
  transition: border-color .2s, background .2s; margin-bottom: 1.5rem;
}
.drop-zone.dragging { border-color: #3a7afe; background: #eef0ff; }

.drop-icon svg { width: 52px; height: 52px; fill: #3a7afe; margin-bottom: 1rem; opacity: .85; }
.drop-text { font-size: 1.1rem; font-weight: 600; color: #1f2937; margin: 0 0 .35rem; }
.drop-hint { color: #9ca3af; font-size: .85rem; margin: 0 0 .8rem; }
.drop-formats { color: #9ca3af; font-size: .8rem; margin: .8rem 0 0; }

.choose-btn {
  background: #3a7afe; color: white; border: none; border-radius: 10px;
  padding: .6rem 1.6rem; font-family: inherit; font-size: .95rem;
  font-weight: 600; cursor: pointer; transition: background .2s, transform .1s;
}
.choose-btn:hover { background: #2e32d4; transform: translateY(-1px); }

/* ── Settings Grid ── */
.settings-grid { margin-bottom: 1.5rem; }
.settings-card { background: white; border-radius: 16px; padding: 1.4rem; border: 1.5px solid #e5e7eb; }

.field-label {
  display: block; font-size: .8rem; font-weight: 600; color: #6b7280;
  text-transform: uppercase; letter-spacing: .06em; margin-bottom: .5rem;
}

/* Select Wrap */
.select-wrap { position: relative; }
.select-wrap select {
  width: 100%; appearance: none; background: #f9fafb; border: 1.5px solid #e5e7eb;
  border-radius: 10px; padding: .8rem 2.2rem .8rem .9rem; font-family: inherit;
  font-size: .95rem; font-weight: 600; color: #374151; cursor: pointer; outline: none; transition: border-color .2s;
}
.select-wrap select:focus { border-color: #3a7afe; }
.select-arrow {
  position: absolute; right: .7rem; top: 50%; transform: translateY(-50%);
  width: 16px; height: 16px; color: #6b7280; pointer-events: none;
}
[dir="rtl"] .select-arrow { right: auto; left: .7rem; }
[dir="rtl"] .select-wrap select { padding: .8rem .9rem .8rem 2.2rem; }

/* ── Action Area ── */
.download-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: .6rem; background: #3a7afe;
  color: white; border: none; border-radius: 10px; padding: .85rem 2.2rem;
  font-family: inherit; font-size: 1rem; font-weight: 700; cursor: pointer;
  transition: background .2s, transform .1s; box-shadow: 0 4px 16px rgba(59,63,232,.3);
}
.download-btn svg { width: 18px; height: 18px; }
.download-btn:hover:not(:disabled) { background: #2e32d4; transform: translateY(-2px); }
.download-btn:disabled { background: #9ca3af; cursor: not-allowed; box-shadow: none; transform: none; }

/* ── File List Container ── */
.file-list-container {
  background: white; border-radius: 16px; padding: 1.5rem; border: 1.5px solid #e5e7eb;
}

.preview-label {
  font-size: .85rem; font-weight: 700; text-transform: uppercase; color: #111827;
}

.reset-icon-btn {
  background: none; border: none; color: #ef4444; font-size: 0.75rem; font-weight: bold; cursor: pointer; padding: 0;
}
.reset-icon-btn:hover { text-decoration: underline; }

.file-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 15px;
}

.file-item {
  display: flex; align-items: center; background: #f9fafb; border: 1.5px solid #e5e7eb;
  border-radius: 12px; padding: 12px; gap: 15px; transition: border-color 0.2s;
}
.file-item:hover { border-color: #cbd5e1; }

.file-thumb {
  width: 50px; height: 50px; border-radius: 8px; object-fit: cover; background: #eee; flex-shrink: 0;
}

.file-info {
  flex-grow: 1; min-width: 0;
}

.file-name {
  font-weight: 600; font-size: 0.9rem; color: #1f2937; margin-bottom: 4px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.file-meta {
  font-size: 0.75rem; font-family: 'DM Mono', monospace; display: flex; align-items: center; gap: 5px;
}

.original-badge { color: #6b7280; background: #e5e7eb; padding: 2px 6px; border-radius: 4px; }
.converted-badge { color: #15803d; background: #dcfce7; padding: 2px 6px; border-radius: 4px; font-weight: bold; }

.file-action {
  flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 36px; height: 36px;
}

.icon-action-btn {
  width: 32px; height: 32px; border-radius: 8px; border: none; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: 0.2s; font-size: 14px; font-weight: bold; text-decoration: none;
}
.icon-action-btn svg { width: 16px; height: 16px; fill: none; }
.icon-action-btn.danger { background: #fee2e2; color: #ef4444; }
.icon-action-btn.danger:hover { background: #f87171; color: white; }
.icon-action-btn.success { background: #3a7afe; color: white; }
.icon-action-btn.success:hover { background: #2e32d4; transform: translateY(-2px); }

.spinner-small {
  width: 20px; height: 20px; border: 3px solid #e5e7eb; border-top-color: #3a7afe;
  border-radius: 50%; animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
